export type DisplayProfileNumericPolicy = {
  min: number;
  max: number;
  default: number;
  step?: number;
};

const OFFSET_ZERO_EPSILON = 0.0001;

export function clampDisplayProfileNumber(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, value));
}

export function snapDisplayProfileNumber(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
  step?: number,
): number {
  const clamped = clampDisplayProfileNumber(value, fallback, min, max);
  if (typeof step !== "number" || !Number.isFinite(step) || step <= 0) {
    return clamped;
  }
  const decimals = Math.max(0, (String(step).split(".")[1] || "").length);
  const snapped = min + Math.round((clamped - min) / step) * step;
  return clampDisplayProfileNumber(
    Number(snapped.toFixed(decimals)),
    fallback,
    min,
    max,
  );
}

export function applyChannelOffsetChange(
  previous: Record<string, number>,
  channelLabel: string,
  rawValue: number,
  policy: DisplayProfileNumericPolicy,
): Record<string, number> {
  const key = channelLabel.trim();
  if (!key) return previous;
  const clamped = clampDisplayProfileNumber(
    rawValue,
    policy.default,
    policy.min,
    policy.max,
  );
  const next = { ...previous };
  if (Math.abs(clamped - policy.default) < OFFSET_ZERO_EPSILON) {
    delete next[key];
  } else {
    next[key] = clamped;
  }
  return next;
}

export function resetChannelOffset(
  previous: Record<string, number>,
  channelLabel: string,
): Record<string, number> {
  const key = channelLabel.trim();
  if (!key || !Object.prototype.hasOwnProperty.call(previous, key)) {
    return previous;
  }
  const next = { ...previous };
  delete next[key];
  return next;
}

export function resetAllChannelOffsets(
  previous: Record<string, number>,
  channelLabels: string[],
): Record<string, number> {
  if (!channelLabels.length) return previous;
  const next = { ...previous };
  channelLabels.forEach((label) => {
    const key = label.trim();
    if (!key) return;
    delete next[key];
  });
  return next;
}

export function normalizeChannelOffsets(
  previous: Record<string, number>,
  channelLabels: string[],
  policy: DisplayProfileNumericPolicy,
): Record<string, number> {
  const activeLabels = channelLabels
    .map((label) => label.trim())
    .filter(
      (label) =>
        label &&
        typeof previous[label] === "number" &&
        Number.isFinite(previous[label]),
    );
  if (!activeLabels.length) {
    return previous;
  }

  const mean =
    activeLabels.reduce((sum, label) => sum + (previous[label] || 0), 0) /
    activeLabels.length;
  const next = { ...previous };

  activeLabels.forEach((label) => {
    const normalized = clampDisplayProfileNumber(
      (previous[label] || 0) - mean,
      policy.default,
      policy.min,
      policy.max,
    );
    if (Math.abs(normalized - policy.default) < OFFSET_ZERO_EPSILON) {
      delete next[label];
    } else {
      next[label] = normalized;
    }
  });

  return next;
}

export function sanitizeChannelPositionOffsets(
  source: Record<string, unknown>,
  policy: DisplayProfileNumericPolicy,
): Record<string, number> {
  const normalized: Record<string, number> = {};
  Object.entries(source).forEach(([channelLabel, offset]) => {
    const key = channelLabel.trim();
    if (!key) return;
    const clamped = clampDisplayProfileNumber(
      offset,
      policy.default,
      policy.min,
      policy.max,
    );
    if (Math.abs(clamped - policy.default) < OFFSET_ZERO_EPSILON) {
      return;
    }
    normalized[key] = clamped;
  });
  return normalized;
}
